import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/apiClient';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Trash2, Star, Check } from 'lucide-react';
import ConfirmDialog from '../ui/ConfirmDialog';
import './AdminDashboard.css';

const IMPORT_TYPE_LABELS = { all_cards: 'All Cards', unique_artwork: 'Unique Artwork', rulings: 'Rulings', oracle_tags: 'Oracle-Tags (Tagger)' };
const importTypeLabel = (type) => IMPORT_TYPE_LABELS[type] ?? type;

// Einheit im Fortschritts-/Abschlusstext je Import-Typ — Karten für die beiden Karten-Bulk-Typen,
// sonst der fachliche Name der importierten Zeilen.
const IMPORT_UNIT_LABELS = { rulings: 'Rulings', oracle_tags: 'Tags' };
const importUnitLabel = (type) => IMPORT_UNIT_LABELS[type] ?? 'Karten';

// ============ API Calls ============

const fetchStats = async () => (await apiClient.get('/api/admin/stats')).data;
const fetchUsers = async () => (await apiClient.get('/api/admin/users')).data;
const fetchAuditLog = async ({ limit = 50, offset = 0 } = {}) =>
  (await apiClient.get(`/api/admin/audit-log?limit=${limit}&offset=${offset}`)).data;
const fetchContactMessages = async () => (await apiClient.get('/api/admin/contact-messages')).data;
const fetchFeedbackMessages = async () => (await apiClient.get('/api/admin/feedback')).data;
const fetchBulkImportStatus = async () => (await apiClient.get('/api/admin/bulk-import/status')).data;

// ============ Component ============

export default function AdminDashboardRoute() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmAction, setConfirmAction] = useState(null); // { type, userId, displayName }
  const [contactOpen, setContactOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null); // id
  const [feedbackToDelete, setFeedbackToDelete] = useState(null); // id

  // --- Data Queries ---
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: fetchStats,
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: fetchUsers,
  });

  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ['admin', 'audit-log'],
    queryFn: () => fetchAuditLog({ limit: 50 }),
  });

  const { data: contactData, isLoading: contactLoading } = useQuery({
    queryKey: ['admin', 'contact-messages'],
    queryFn: fetchContactMessages,
  });

  const markReadMutation = useMutation({
    mutationFn: (id) => apiClient.patch(`/api/admin/contact-messages/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'contact-messages'] }),
  });

  const deleteContactMutation = useMutation({
    mutationFn: (id) => apiClient.delete(`/api/admin/contact-messages/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'contact-messages'] }),
  });

  // --- Feedback ---
  const { data: feedbackData, isLoading: feedbackLoading } = useQuery({
    queryKey: ['admin', 'feedback'],
    queryFn: fetchFeedbackMessages,
  });

  const updateFeedbackStatusMutation = useMutation({
    mutationFn: ({ id, status }) => apiClient.patch(`/api/admin/feedback/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'feedback'] }),
  });

  const updateFeedbackPriorityMutation = useMutation({
    mutationFn: ({ id, priority }) => apiClient.patch(`/api/admin/feedback/${id}/priority`, { priority }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'feedback'] }),
  });

  const deleteFeedbackMutation = useMutation({
    mutationFn: (id) => apiClient.delete(`/api/admin/feedback/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'feedback'] }),
  });

  // --- Bulk Import ---
  const { data: bulkStatus, isLoading: bulkLoading } = useQuery({
    queryKey: ['admin', 'bulk-import-status'],
    queryFn: fetchBulkImportStatus,
    refetchInterval: (query) => {
      const active = query.state.data?.activeImport;
      return active?.status === 'running' ? 3000 : false;
    },
  });

  const startImportMutation = useMutation({
    mutationFn: (type) => apiClient.post('/api/admin/bulk-import/start', { type }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'bulk-import-status'] }),
  });

  const dismissErrorMutation = useMutation({
    mutationFn: () => apiClient.post('/api/admin/bulk-import/dismiss-error'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'bulk-import-status'] }),
  });

  // --- Mutations ---
  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin'] });
  };

  const toggleRegMutation = useMutation({
    mutationFn: (enabled) => apiClient.post('/api/admin/settings/toggle-registration', { enabled }),
    onSuccess: invalidateAll,
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, newRole }) => apiClient.post('/api/admin/users/role', { userId, newRole }),
    onSuccess: invalidateAll,
  });

  const resetAccountMutation = useMutation({
    mutationFn: (userId) => apiClient.post('/api/admin/users/reset', { userId }),
    onSuccess: () => { invalidateAll(); setConfirmAction(null); },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId) => apiClient.delete(`/api/admin/users/${userId}`),
    onSuccess: () => { invalidateAll(); setConfirmAction(null); },
  });

  // --- Handlers ---
  const handleRoleChange = useCallback((userId, newRole) => {
    updateRoleMutation.mutate({ userId, newRole });
  }, [updateRoleMutation]);

  const handleConfirmAction = useCallback(() => {
    if (!confirmAction) return;
    // Der Bestätigen-Button des ConfirmDialog bleibt während der Anfrage klickbar —
    // ohne diesen Guard stößt ein zweiter Klick die Mutation erneut an.
    if (resetAccountMutation.isPending || deleteUserMutation.isPending) return;
    if (confirmAction.type === 'reset') {
      resetAccountMutation.mutate(confirmAction.userId);
    } else if (confirmAction.type === 'delete') {
      deleteUserMutation.mutate(confirmAction.userId);
    }
  }, [confirmAction, resetAccountMutation, deleteUserMutation]);

  const users = usersData?.users ?? [];
  const logs = auditData?.logs ?? [];
  const contactMessages = contactData?.messages ?? [];
  const feedbackMessages = feedbackData?.messages ?? [];

  const feedbackTypeLabel = (t) => ({ feedback: 'Feedback', bug: 'Bug gefunden', feature: 'Feature-Wunsch' }[t] || t);
  const feedbackTypeBadgeClass = (t) => ({ feedback: 'admin-feedback-type-feedback', bug: 'admin-feedback-type-bug', feature: 'admin-feedback-type-feature' }[t] || '');

  const nextPriority = (current) => {
    if (current === 'normal') return 'important';
    if (current === 'important') return 'unimportant';
    return 'normal';
  };

  // Sort feedback: important first, then by date
  const sortedFeedback = [...feedbackMessages].sort((a, b) => {
    if (a.priority === 'important' && b.priority !== 'important') return -1;
    if (b.priority === 'important' && a.priority !== 'important') return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const roleBadgeClass = (role) => {
    if (role === 'admin') return 'admin-badge-admin';
    if (role === 'pro') return 'admin-badge-pro';
    return 'admin-badge-standard';
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '—';
    if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
    return `${(bytes / 1_000_000).toFixed(0)} MB`;
  };

  const formatDuration = (durationMs) => {
    if (!durationMs) return '—';
    const totalSeconds = Math.floor(durationMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const formatAuditDetails = (action, details) => {
    if (!details) return {};
    try {
      const parsed = typeof details === 'string' ? JSON.parse(details) : details;
      
      // Spezialformatierung für Bulk Import Actions
      if (action === 'bulk_import_completed' && parsed) {
        return {
          type: importTypeLabel(parsed.type),
          unit: importUnitLabel(parsed.type),
          duration: formatDuration(parsed.durationMs),
          cardsImported: (parsed.cardsImported ?? parsed.rulingsImported ?? parsed.tagsUpserted)?.toLocaleString('de-DE') || '—',
        };
      }
      if (action === 'bulk_import_started' && parsed) {
        return { type: importTypeLabel(parsed.type) };
      }
      if (action === 'bulk_import_failed' && parsed) {
        return {
          type: importTypeLabel(parsed.type),
          duration: formatDuration(parsed.durationMs),
          error: parsed.error || '—',
        };
      }

      // Spezialformatierung für Patch Note Actions
      if (action === 'patch_note_created' && parsed) {
        return {
          patchNoteId: parsed.patchNoteId || '—',
          title: parsed.title || '—',
        };
      }
      if (action === 'patch_note_updated' && parsed) {
        return {
          patchNoteId: parsed.patchNoteId || '—',
          title: parsed.title || '—',
        };
      }
      if (action === 'patch_note_published' && parsed) {
        return {
          patchNoteId: parsed.patchNoteId || '—',
          title: parsed.title || '—',
        };
      }
      if (action === 'patch_note_unpublished' && parsed) {
        return {
          patchNoteId: parsed.patchNoteId || '—',
        };
      }
      if (action === 'patch_note_deleted' && parsed) {
        return {
          patchNoteId: parsed.patchNoteId || '—',
          title: parsed.title || '—',
        };
      }

      // Spezialformatierung für User-Management Actions
      if (action === 'update_user_role' && parsed) {
        return {
          newRole: parsed.newRole || '—',
        };
      }
      if (action === 'toggle_registration' && parsed) {
        return {
          enabled: parsed.enabled ? 'Aktiviert' : 'Deaktiviert',
        };
      }
      if (action === 'reset_user_account' && parsed) {
        return {
          message: parsed.message || 'Account zurückgesetzt',
        };
      }
      if (action === 'delete_user' && parsed) {
        return {
          email: parsed.email || '—',
          displayName: parsed.displayName || '—',
        };
      }

      // Spezialformatierung für Login-Fehler
      if (action === 'login_failed' && parsed) {
        const reasonLabel = {
          user_not_found: 'Benutzer nicht gefunden',
          invalid_password: 'Falsches Passwort',
        }[parsed.reason] || parsed.reason || 'Unbekannter Grund';
        return {
          email: parsed.email || '—',
          reason: reasonLabel,
        };
      }
      
      return parsed;
    } catch (e) {
      return details || {};
    }
  };

  const getAuditDetailsSummary = (action, details) => {
    const formatted = formatAuditDetails(action, details);
    
    // Bulk Import Actions
    if (action === 'bulk_import_completed') {
      return `${formatted.type} • ${formatted.cardsImported} ${formatted.unit} • ${formatted.duration}`;
    }
    if (action === 'bulk_import_started') {
      return `${formatted.type}`;
    }
    if (action === 'bulk_import_failed') {
      return `${formatted.type} • Fehler: ${formatted.error} • ${formatted.duration}`;
    }

    // Patch Note Actions
    if (action === 'patch_note_created') {
      return `#${formatted.patchNoteId} • "${formatted.title}"`;
    }
    if (action === 'patch_note_updated') {
      return `#${formatted.patchNoteId} • "${formatted.title}"`;
    }
    if (action === 'patch_note_published') {
      return `#${formatted.patchNoteId} • "${formatted.title}"`;
    }
    if (action === 'patch_note_unpublished') {
      return `#${formatted.patchNoteId}`;
    }
    if (action === 'patch_note_deleted') {
      return `#${formatted.patchNoteId} • "${formatted.title}"`;
    }

    // User Management Actions
    if (action === 'update_user_role') {
      return `Neue Rolle: ${formatted.newRole}`;
    }
    if (action === 'toggle_registration') {
      return `${formatted.enabled}`;
    }
    if (action === 'reset_user_account') {
      return formatted.message;
    }
    if (action === 'delete_user') {
      return `${formatted.displayName} (${formatted.email})`;
    }

    // Login Failed
    if (action === 'login_failed') {
      return `${formatted.email} • ${formatted.reason}`;
    }
    
    // Default: JSON stringified details
    try {
      return JSON.stringify(formatted, null, 0);
    } catch {
      return details || '';
    }
  };

  const isOutdated = (scryfallUpdatedAt, lastImportedAt) => {
    if (!lastImportedAt) return true;
    return new Date(scryfallUpdatedAt) > new Date(lastImportedAt);
  };

  const activeImport = bulkStatus?.activeImport;
  const isImportRunning = activeImport?.status === 'running';
  const isImportError = activeImport?.status === 'error';
  const isImportCompleted = activeImport?.status === 'completed';

  const importErrorSum = (ec) =>
    ec ? (ec.cardPrintLocalization || 0) + (ec.cardLocalization || 0) + (ec.cardPrintFace || 0) : 0;

  // "card_print_localization 12 · card_localization 3" — nur Tabellen mit Fehlern
  const importErrorLabel = (ec) =>
    [['card_print_localization', ec?.cardPrintLocalization], ['card_localization', ec?.cardLocalization], ['card_print_face', ec?.cardPrintFace]]
      .filter(([, n]) => n > 0)
      .map(([table, n]) => `${table} ${n.toLocaleString('de-DE')}`)
      .join(' · ');

  const formatDurationMs = (ms) => {
    if (ms == null) return '';
    const totalSec = Math.round(ms / 1000);
    if (totalSec < 60) return `${totalSec} s`;
    return `${Math.round(totalSec / 60)} Min.`;
  };

  const getElapsedTime = (startedAt) => {
    if (!startedAt) return '';
    const seconds = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m === 0) return `${s}s`;
    return `${m}m ${s}s`;
  };

  const actionLabel = (action) => {
    const labels = {
      update_user_role: 'Rolle geändert',
      reset_user_account: 'Account zurückgesetzt',
      delete_user: 'User gelöscht',
      toggle_registration: 'Registrierung geändert',
      login_failed: 'Login fehlgeschlagen',
      patch_note_created: 'Patch Note erstellt',
      patch_note_published: 'Patch Note veröffentlicht',
      patch_note_unpublished: 'Patch Note zurückgezogen',
      patch_note_deleted: 'Patch Note gelöscht',
      bulk_import_started: 'Bulk Import gestartet',
      bulk_import_completed: 'Bulk Import abgeschlossen',
      bulk_import_failed: 'Bulk Import fehlgeschlagen',
    };
    return labels[action] || action;
  };

  return (
    <div className="admin-dashboard">
      <h1>Admin Dashboard</h1>
      <div className="admin-nav-links">
        <button className="btn-secondary" onClick={() => navigate('/admin/patch-notes')}>
          📋 Patch Notes verwalten
        </button>
      </div>

      {/* ========== System Stats ========== */}
      <section className="admin-section">
        <h2>System-Statistiken</h2>
        {statsLoading ? (
          <p className="admin-loading">Lädt...</p>
        ) : stats ? (
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <span className="admin-stat-value">{stats.users?.totalUsers ?? 0}</span>
              <span className="admin-stat-label">Benutzer gesamt</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-value">{stats.users?.activeUsers ?? 0}</span>
              <span className="admin-stat-label">Aktive Benutzer</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-value">{stats.users?.adminUsers ?? 0}</span>
              <span className="admin-stat-label">Admins</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-value">{stats.users?.proUsers ?? 0}</span>
              <span className="admin-stat-label">Pro-User</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-value">{stats.collections?.totalBinders ?? 0}</span>
              <span className="admin-stat-label">Binders</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-value">{stats.collections?.totalDecks ?? 0}</span>
              <span className="admin-stat-label">Decks</span>
            </div>
          </div>
        ) : null}

        {/* Registration Toggle */}
        {stats && (
          <div className="admin-registration-toggle">
            <span>Registrierung:</span>
            <button
              className={`admin-toggle-btn ${stats.registrationEnabled ? 'admin-toggle-on' : 'admin-toggle-off'}`}
              onClick={() => toggleRegMutation.mutate(!stats.registrationEnabled)}
              disabled={toggleRegMutation.isPending}
            >
              {stats.registrationEnabled ? 'Aktiviert' : 'Deaktiviert'}
            </button>
          </div>
        )}
      </section>

      {/* ========== Bulk Import ========== */}
      <section className="admin-section">
        <h2>Scryfall Bulk-Import</h2>
        {bulkLoading ? (
          <p className="admin-loading">Lädt Scryfall-Status...</p>
        ) : !bulkStatus?.scryfallTypes?.length ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Scryfall-Metadaten konnten nicht geladen werden.</p>
        ) : (
          <>
            {/* Aktiver Import / Fehler */}
            {isImportRunning && (
              <div className="admin-bulk-progress">
                <span className="admin-bulk-progress-spinner" />
                <span>
                  <strong>{importTypeLabel(activeImport.type)}</strong>
                  {' — '}{activeImport.progress?.toLocaleString('de-DE') ?? 0} {importUnitLabel(activeImport.type)} verarbeitet
                  {activeImport.startedAt && ` (${getElapsedTime(activeImport.startedAt)})`}
                  {importErrorSum(activeImport.errorCounts) > 0 && (
                    <span className="admin-bulk-inline-errors">
                      {' '}· {importErrorSum(activeImport.errorCounts).toLocaleString('de-DE')} Fehler
                    </span>
                  )}
                </span>
              </div>
            )}
            {isImportCompleted && (
              <div className="admin-bulk-completed">
                <span>
                  <strong>Import abgeschlossen</strong>
                  {' — '}{(activeImport.processed ?? 0).toLocaleString('de-DE')} {importUnitLabel(activeImport.type)}
                  {activeImport.durationMs != null && `, ${formatDurationMs(activeImport.durationMs)}`}
                  {importErrorSum(activeImport.errorCounts) > 0 && (
                    <span className="admin-bulk-inline-errors">
                      {' '}· Fehler: {importErrorLabel(activeImport.errorCounts)}
                    </span>
                  )}
                </span>
                <button
                  className="admin-action-btn"
                  onClick={() => dismissErrorMutation.mutate()}
                  aria-label="Import-Ergebnis ausblenden"
                >
                  ×
                </button>
              </div>
            )}
            {isImportError && (
              <div className="admin-bulk-error">
                <span>
                  Import fehlgeschlagen: {activeImport.error}
                  {importErrorSum(activeImport.errorCounts) > 0 &&
                    ` — zuvor gezählte Upsert-Fehler (mindestens): ${importErrorLabel(activeImport.errorCounts)}`}
                </span>
                <button className="admin-action-btn" onClick={() => dismissErrorMutation.mutate()}>Verwerfen</button>
              </div>
            )}

            <div className="admin-bulk-grid">
              {bulkStatus.scryfallTypes.map((st) => {
                const lastImported = bulkStatus.lastImports?.[st.type] ?? null;
                const outdated = isOutdated(st.updatedAt, lastImported);
                const isThisRunning = isImportRunning && activeImport.type === st.type;

                return (
                  <div
                    key={st.type}
                    className={`admin-bulk-card ${outdated ? 'admin-bulk-outdated' : 'admin-bulk-uptodate'}`}
                  >
                    <div className="admin-bulk-card-header">
                      <span className="admin-bulk-card-name">{st.name}</span>
                      <span className="admin-bulk-card-size">{formatBytes(st.size)}</span>
                    </div>
                    <p className="admin-bulk-card-desc">{st.description}</p>
                    <div className="admin-bulk-timestamps">
                      <div className="admin-bulk-ts-row">
                        <span className="admin-bulk-ts-label">Scryfall:</span>
                        <span className="admin-bulk-ts-value">{formatDate(st.updatedAt)}</span>
                      </div>
                      <div className="admin-bulk-ts-row">
                        <span className="admin-bulk-ts-label">Importiert:</span>
                        <span className={`admin-bulk-ts-value ${outdated ? 'admin-bulk-ts-outdated' : ''}`}>
                          {lastImported ? formatDate(lastImported) : 'Noch nie'}
                        </span>
                      </div>
                    </div>
                    <button
                      className="admin-bulk-start-btn"
                      disabled={isImportRunning || startImportMutation.isPending}
                      onClick={() => startImportMutation.mutate(st.type)}
                    >
                      {isThisRunning ? 'Läuft...' : outdated ? 'Jetzt importieren' : 'Erneut importieren'}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* ========== Users Table ========== */}
      <section className="admin-section">
        <h2>Benutzer-Verwaltung</h2>
        {usersLoading ? (
          <p className="admin-loading">Lädt...</p>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>E-Mail</th>
                  <th>Anzeigename</th>
                  <th>Rolle</th>
                  <th>Letzter Login</th>
                  <th>Binders</th>
                  <th>Decks</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.email}</td>
                    <td>{u.displayName}</td>
                    <td>
                      <select
                        className={`admin-role-select ${roleBadgeClass(u.role)}`}
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        disabled={updateRoleMutation.isPending}
                      >
                        <option value="standard">Standard</option>
                        <option value="pro">Pro</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>{formatDate(u.lastLogin)}</td>
                    <td>{u.totalBinders ?? 0}</td>
                    <td>{u.totalDecks ?? 0}</td>
                    <td className="admin-actions-cell">
                      <button
                        className="admin-action-btn admin-action-reset"
                        onClick={() => setConfirmAction({ type: 'reset', userId: u.id, displayName: u.displayName || u.email })}
                        title="Account zurücksetzen"
                      >
                        Zurücksetzen
                      </button>
                      <button
                        className="admin-action-btn admin-action-delete"
                        onClick={() => setConfirmAction({ type: 'delete', userId: u.id, displayName: u.displayName || u.email })}
                        title="Account löschen"
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ========== Contact Messages (collapsible) ========== */}
      <section className="admin-section">
        <div className="admin-section-header" onClick={() => setContactOpen(!contactOpen)}>
          <div className="admin-section-header-left">
            {contactOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <h2>Kontaktnachrichten</h2>
          </div>
          {contactMessages.filter(m => !m.read).length > 0 && (
            <span className="admin-badge-unread">{contactMessages.filter(m => !m.read).length} neu</span>
          )}
        </div>
        {contactOpen && (
          contactLoading ? (
            <p className="admin-loading">Lädt...</p>
          ) : contactMessages.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Keine Nachrichten vorhanden.</p>
          ) : (
            <div className="admin-contact-list">
              {contactMessages.map((msg) => (
                <div key={msg.id} className={`admin-contact-card ${msg.read ? 'admin-contact-read' : 'admin-contact-unread'}`}>
                  <div className="admin-contact-header">
                    <div>
                      <strong>{msg.name}</strong>
                      <a href={`mailto:${msg.email}`} style={{ marginLeft: '8px', color: 'var(--accent)', fontSize: 'var(--text-xs)' }}>{msg.email}</a>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>{formatDate(msg.createdAt)}</span>
                      {!msg.read && (
                        <button
                          className="admin-action-btn"
                          style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
                          onClick={() => markReadMutation.mutate(msg.id)}
                          disabled={markReadMutation.isPending}
                        >
                          Gelesen
                        </button>
                      )}
                      <button
                        className="admin-feedback-action-btn admin-action-delete"
                        title="Löschen"
                        onClick={() => setContactToDelete(msg.id)}
                        disabled={deleteContactMutation.isPending}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="admin-contact-body">{msg.message}</p>
                </div>
              ))}
            </div>
          )
        )}
      </section>

      {/* ========== Feedback Messages (collapsible) ========== */}
      <section className="admin-section">
        <div className="admin-section-header" onClick={() => setFeedbackOpen(!feedbackOpen)}>
          <div className="admin-section-header-left">
            {feedbackOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <h2>Feedback & Fehlerberichte</h2>
          </div>
          {feedbackMessages.filter(m => m.status === 'new').length > 0 && (
            <span className="admin-badge-unread">{feedbackMessages.filter(m => m.status === 'new').length} neu</span>
          )}
        </div>
        {feedbackOpen && (
          feedbackLoading ? (
            <p className="admin-loading">Lädt...</p>
          ) : sortedFeedback.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Kein Feedback vorhanden.</p>
          ) : (
            <div className="admin-contact-list">
              {sortedFeedback.map((msg) => (
                <div
                  key={msg.id}
                  className={`admin-contact-card ${
                    msg.status === 'new' ? 'admin-contact-unread' :
                    msg.status === 'done' ? 'admin-contact-read' : ''
                  } ${
                    msg.priority === 'important' ? 'admin-feedback-important' :
                    msg.priority === 'unimportant' ? 'admin-feedback-unimportant' : ''
                  }`}
                >
                  <div className="admin-contact-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span className={`admin-feedback-badge ${feedbackTypeBadgeClass(msg.type)}`}>
                        {feedbackTypeLabel(msg.type)}
                      </span>
                      <strong>{msg.userName || 'Unbekannt'}</strong>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>Seite: {msg.page}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>{formatDate(msg.createdAt)}</span>
                    </div>
                  </div>
                  <p className="admin-contact-body">{msg.message}</p>
                  <div className="admin-feedback-actions">
                    {msg.status === 'new' && (
                      <button
                        className="admin-action-btn"
                        style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
                        onClick={() => updateFeedbackStatusMutation.mutate({ id: msg.id, status: 'read' })}
                        disabled={updateFeedbackStatusMutation.isPending}
                      >
                        Gelesen
                      </button>
                    )}
                    {msg.status !== 'done' && (
                      <button
                        className="admin-feedback-action-btn"
                        title="Als erledigt markieren"
                        onClick={() => updateFeedbackStatusMutation.mutate({ id: msg.id, status: 'done' })}
                        disabled={updateFeedbackStatusMutation.isPending}
                      >
                        <Check size={14} />
                      </button>
                    )}
                    {msg.status === 'done' && (
                      <button
                        className="admin-action-btn"
                        style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
                        onClick={() => updateFeedbackStatusMutation.mutate({ id: msg.id, status: 'new' })}
                        disabled={updateFeedbackStatusMutation.isPending}
                      >
                        Wieder öffnen
                      </button>
                    )}
                    <button
                      className={`admin-feedback-action-btn ${msg.priority === 'important' ? 'admin-feedback-star-active' : ''}`}
                      title={`Priorität: ${msg.priority} (klicken zum Wechseln)`}
                      onClick={() => updateFeedbackPriorityMutation.mutate({ id: msg.id, priority: nextPriority(msg.priority) })}
                      disabled={updateFeedbackPriorityMutation.isPending}
                    >
                      <Star size={14} fill={msg.priority === 'important' ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      className="admin-feedback-action-btn admin-action-delete"
                      title="Löschen"
                      onClick={() => setFeedbackToDelete(msg.id)}
                      disabled={deleteFeedbackMutation.isPending}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </section>

      {/* ========== Audit Log ========== */}
      <section className="admin-section">
        <h2>Audit-Log</h2>
        {auditLoading ? (
          <p className="admin-loading">Lädt...</p>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table admin-audit-table">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Admin</th>
                  <th>Aktion</th>
                  <th>Ziel-User</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const detailsSummary = getAuditDetailsSummary(log.action, log.details);
                  const hasFormattedDetails = [
                    'bulk_import_started', 'bulk_import_completed', 'bulk_import_failed',
                    'patch_note_created', 'patch_note_updated', 'patch_note_published', 'patch_note_unpublished', 'patch_note_deleted',
                    'update_user_role', 'toggle_registration', 'reset_user_account', 'delete_user', 'login_failed',
                  ].includes(log.action);
                  
                  return (
                    <tr key={log.id} className={log.action === 'login_failed' || log.action === 'bulk_import_failed' ? 'admin-row-danger' : ''}>
                      <td>{formatDate(log.createdAt)}</td>
                      <td>{log.adminEmail || (log.adminId ? `#${log.adminId}` : '—')}</td>
                      <td>{actionLabel(log.action)}</td>
                      <td>{log.targetUserId ? `#${log.targetUserId}` : '—'}</td>
                      <td className="admin-details-cell" title={hasFormattedDetails ? detailsSummary : log.details}>{detailsSummary}</td>
                    </tr>
                  );
                })}
                {logs.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Keine Einträge</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(o) => { if (!o) setConfirmAction(null); }}
        title={
          confirmAction?.type === 'delete' ? 'Benutzer löschen'
            : 'Account zurücksetzen'
        }
        description={
          confirmAction?.type === 'delete'
            ? `Möchtest du den Account von "${confirmAction?.displayName}" wirklich unwiderruflich löschen? Alle Daten werden entfernt.`
            : `Möchtest du alle Sammlungen und Decks von "${confirmAction?.displayName}" wirklich löschen? Der Account bleibt bestehen.`
        }
        confirmLabel={(resetAccountMutation.isPending || deleteUserMutation.isPending) ? 'Lädt...' : 'Bestätigen'}
        tone={confirmAction?.type === 'delete' ? 'danger' : 'default'}
        onConfirm={handleConfirmAction}
      />

      <ConfirmDialog
        open={!!contactToDelete}
        onOpenChange={(o) => { if (!o) setContactToDelete(null); }}
        title="Kontaktnachricht löschen?"
        description="Die Nachricht wird unwiderruflich gelöscht."
        confirmLabel="Löschen"
        tone="danger"
        onConfirm={() => { deleteContactMutation.mutate(contactToDelete); setContactToDelete(null); }}
      />
      <ConfirmDialog
        open={!!feedbackToDelete}
        onOpenChange={(o) => { if (!o) setFeedbackToDelete(null); }}
        title="Feedback löschen?"
        description="Der Eintrag wird unwiderruflich gelöscht."
        confirmLabel="Löschen"
        tone="danger"
        onConfirm={() => { deleteFeedbackMutation.mutate(feedbackToDelete); setFeedbackToDelete(null); }}
      />
    </div>
  );
}
